/**
 * CostSchemeTermsPanel — the right-hand "Termíny" rail shown only in the list view of a term series
 * (`scope === 'serie'`). Lists all terms of the series grouped by month; each row shows a calendar mark,
 * the term date range (with weekday), pax, a shared/own-scheme lock icon, and the Pax / Grp / Tot cost
 * groups (calculation · on-site) in the domestic currency.
 *
 * Terms with the SHARED series scheme take the live series totals (they update as the scheme is edited);
 * terms with their OWN scheme carry their stored totals from the async endpoint. Styled with Tailwind only,
 * matching the source design (tp-terms).
 */
import { useMemo, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Share2, SlidersVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { SerieCostSchemeTermItem } from '@/api/generated/schemas';
import { useGetVenuaPlannerSerieCostSchemeTerms } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { cn } from '@/shared/lib/utils';
import { Money } from '@/shared/ui';
import type { CostTotals } from '../model';

interface CostSchemeTermsPanelProps {
  productId: string;
  serieId: string;
  /** Živý souhrn nákladů série (v domácí měně) – přebírají ho termíny se sdíleným schématem. */
  liveTotals: CostTotals;
  /** Plánovaný počet osob série (dělitel skupinových nákladů u sdílených termínů). */
  pax: number;
}

const MONTH_FORMAT = new Intl.DateTimeFormat('cs-CZ', { month: 'long' });
const CS_DOW = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];

/** „1.11." / „27.12.2026" — přesně jako fmtTermDate v design projektu. */
function fmtTermDate(iso: string, withYear = false): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${parseInt(d, 10)}.${parseInt(m, 10)}.${withYear ? y : ''}`;
}

function dowOf(iso: string): string {
  return CS_DOW[new Date(`${iso.slice(0, 10)}T00:00:00`).getDay()];
}

interface TermFigures {
  pax: number;
  paxCalc: number;
  paxOnsite: number;
  grpCalc: number;
  grpOnsite: number;
  totCalc: number;
  totOnsite: number;
}

function figuresFor(
  term: SerieCostSchemeTermItem,
  liveTotals: CostTotals,
  seriePax: number
): TermFigures {
  const paxCount = term.pax > 0 ? term.pax : seriePax > 0 ? seriePax : 1;
  const paxCalc = term.isShared ? liveTotals.paxCalc : (term.totals?.paxCalculation ?? 0);
  const paxOnsite = term.isShared ? liveTotals.paxOnsite : (term.totals?.paxOnsite ?? 0);
  const grpCalc = term.isShared ? liveTotals.grpCalc : (term.totals?.groupCalculation ?? 0);
  const grpOnsite = term.isShared ? liveTotals.grpOnsite : (term.totals?.groupOnsite ?? 0);
  return {
    pax: paxCount,
    paxCalc,
    paxOnsite,
    grpCalc,
    grpOnsite,
    totCalc: paxCalc + grpCalc / paxCount,
    totOnsite: paxOnsite + grpOnsite / paxCount,
  };
}

/** Jedna nákladová skupina: uppercase label nad dvojicí kalkulační · na místě. */
function TermCost({ label, calc, onsite }: { label: string; calc: number; onsite: number }) {
  return (
    <span className="inline-flex flex-col gap-px">
      <span className="text-phx-text-muted text-[8px] font-semibold tracking-wide whitespace-nowrap uppercase">
        {label}
      </span>
      <span className="gap-phx-1_5 text-phx-xs inline-flex items-baseline whitespace-nowrap tabular-nums">
        <Money value={calc} decimals={0} className="text-phx-info font-semibold" />
        <span className="text-phx-text-muted">·</span>
        <Money value={onsite} decimals={0} className="text-phx-warning font-semibold" />
      </span>
    </span>
  );
}

export function CostSchemeTermsPanel({
  productId,
  serieId,
  liveTotals,
  pax,
}: CostSchemeTermsPanelProps) {
  const { t } = useTranslation('venua-planner');
  const [collapsed, setCollapsed] = useState(false);
  const query = useGetVenuaPlannerSerieCostSchemeTerms(productId, serieId);

  const terms = useMemo(() => query.data?.terms ?? [], [query.data?.terms]);

  const groups = useMemo(() => {
    const byKey = new Map<
      string,
      { key: string; label: string; year: string; items: SerieCostSchemeTermItem[] }
    >();
    const order: string[] = [];
    for (const term of terms) {
      const key = term.dateFrom.slice(0, 7);
      let group = byKey.get(key);
      if (!group) {
        const date = new Date(`${term.dateFrom.slice(0, 10)}T00:00:00`);
        group = {
          key,
          label: MONTH_FORMAT.format(date),
          year: term.dateFrom.slice(0, 4),
          items: [],
        };
        byKey.set(key, group);
        order.push(key);
      }
      group.items.push(term);
    }
    return order.map((key) => byKey.get(key)!);
  }, [terms]);

  const sharedCount = terms.filter((term) => term.isShared).length;
  const customCount = terms.length - sharedCount;

  if (collapsed) {
    return (
      <aside className="border-phx-border bg-phx-surface-2 flex w-10 shrink-0 flex-col items-center gap-2.5 overflow-hidden border-l pt-2">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          title={t('costScheme.terms.expand')}
          aria-label={t('costScheme.terms.expand')}
          className="border-phx-border bg-phx-surface text-phx-text-2 hover:bg-phx-surface-2 hover:text-phx-text rounded-phx-sm flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center border"
        >
          <ChevronLeft size={15} />
        </button>
        <span className="text-phx-text-2 gap-phx-3_5 text-phx-sm flex rotate-180 items-center font-bold whitespace-nowrap [writing-mode:vertical-rl]">
          {t('costScheme.terms.title')}
          <span className="text-phx-text-muted font-semibold tabular-nums">{terms.length}</span>
        </span>
      </aside>
    );
  }

  return (
    <aside className="border-phx-border bg-phx-surface flex w-160 shrink-0 flex-col border-l">
      {/* head */}
      <div className="border-phx-border bg-phx-surface-2 shrink-0 border-b px-3.5 pt-3 pb-[11px]">
        <div className="flex items-center gap-2">
          <span className="text-phx-text text-phx-md font-bold">{t('costScheme.terms.title')}</span>
          <span className="text-phx-text-2 border-phx-border bg-phx-surface-3 px-phx-3_5 text-phx-xs rounded-full border leading-4 font-semibold tabular-nums">
            {terms.length}
          </span>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            title={t('costScheme.terms.collapse')}
            aria-label={t('costScheme.terms.collapse')}
            className="border-phx-border bg-phx-surface text-phx-text-2 hover:bg-phx-surface-2 hover:text-phx-text rounded-phx-sm ml-auto flex h-6 w-6 cursor-pointer items-center justify-center border"
          >
            <ChevronRight size={15} />
          </button>
        </div>
        <div className="mt-[9px] flex items-center gap-3.5">
          <span className="text-phx-text-2 gap-phx-2_5 text-phx-2xs inline-flex items-center">
            <Share2 size={12} className="text-phx-primary" />
            {t('costScheme.terms.shared')}
          </span>
          <span className="text-phx-text-2 gap-phx-2_5 text-phx-2xs inline-flex items-center">
            <SlidersVertical size={12} className="text-phx-warning" />
            {t('costScheme.terms.custom')}
          </span>
          <span className="text-phx-text-muted gap-phx-2_5 text-phx-2xs ml-auto inline-flex items-center">
            <span className="text-phx-info">{t('costScheme.terms.calc')}</span>
            <span className="text-phx-text-muted">·</span>
            <span className="text-phx-warning">{t('costScheme.terms.onsite')}</span>
          </span>
        </div>
      </div>

      {/* scroll */}
      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {query.isLoading ? (
          <p className="text-phx-text-muted text-phx-sm px-3.5 py-3">
            {t('costScheme.terms.loading')}
          </p>
        ) : query.isError ? (
          <p className="text-phx-danger text-phx-sm px-3.5 py-3">{t('costScheme.terms.error')}</p>
        ) : terms.length === 0 ? (
          <p className="text-phx-text-muted text-phx-sm px-3.5 py-3">
            {t('costScheme.terms.empty')}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.key}>
              <div className="border-phx-border/70 bg-phx-surface-2 text-phx-text-muted tracking-phx-wide text-phx-2xs sticky top-0 z-[2] flex items-center justify-between border-b px-3.5 pt-2.5 pb-1 font-semibold uppercase first:pt-2">
                <span>
                  {group.label}{' '}
                  <span className="text-phx-text-muted/75 font-normal tabular-nums">
                    {group.year}
                  </span>
                </span>
                <span className="tabular-nums">{group.items.length}</span>
              </div>
              {group.items.map((term) => {
                const figures = figuresFor(term, liveTotals, pax);
                const custom = !term.isShared;
                // 0noční termín (od == do) → jen jedno datum s rokem + den v týdnu, bez šipky.
                const sameDay =
                  !term.dateTo || term.dateTo.slice(0, 10) === term.dateFrom.slice(0, 10);
                return (
                  <div
                    key={term.termId}
                    title={
                      custom ? t('costScheme.terms.customTitle') : t('costScheme.terms.sharedTitle')
                    }
                    className="border-phx-border/55 hover:bg-phx-surface-2 flex items-center gap-[9px] border-t px-3.5 py-1.5 transition-colors"
                  >
                    <span
                      className={cn(
                        'flex shrink-0 items-center',
                        custom ? 'text-phx-warning' : 'text-phx-primary'
                      )}
                      aria-hidden="true"
                    >
                      <Calendar size={14} />
                    </span>
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      <div
                        className={cn(
                          'gap-phx-2_5 text-phx-xs flex flex-none items-baseline font-mono font-medium whitespace-nowrap tabular-nums',
                          custom ? 'text-phx-text-2' : 'text-phx-text'
                        )}
                      >
                        {sameDay ? (
                          <span>
                            {fmtTermDate(term.dateFrom, true)}{' '}
                            <span className="text-phx-text-muted font-normal">
                              ({dowOf(term.dateFrom)})
                            </span>
                          </span>
                        ) : (
                          <>
                            <span>
                              {fmtTermDate(term.dateFrom)}{' '}
                              <span className="text-phx-text-muted font-normal">
                                ({dowOf(term.dateFrom)})
                              </span>
                            </span>
                            <span className="text-phx-text-muted [font-family:var(--phx-font-sans)]">
                              →
                            </span>
                            <span>{fmtTermDate(term.dateTo!, true)}</span>
                          </>
                        )}
                      </div>
                      <div className="text-phx-text-muted text-phx-2xs flex-none whitespace-nowrap">
                        <b className="text-phx-text-2 font-bold tabular-nums">{figures.pax}</b>{' '}
                        {t('costScheme.terms.pax')}
                      </div>
                      <div className="flex flex-none items-center">
                        {custom ? (
                          <SlidersVertical size={13} className="text-phx-warning" />
                        ) : (
                          <Share2 size={13} className="text-phx-primary" />
                        )}
                      </div>
                      <div
                        className={cn(
                          'ml-auto flex flex-none flex-nowrap gap-[18px]',
                          custom && 'opacity-[0.88]'
                        )}
                      >
                        <TermCost
                          label={t('costScheme.terms.colPax')}
                          calc={figures.paxCalc}
                          onsite={figures.paxOnsite}
                        />
                        <TermCost
                          label={t('costScheme.terms.colGrp')}
                          calc={figures.grpCalc}
                          onsite={figures.grpOnsite}
                        />
                        <TermCost
                          label={`${t('costScheme.terms.colTot')} / ${figures.pax}`}
                          calc={figures.totCalc}
                          onsite={figures.totOnsite}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* foot */}
      <div className="border-phx-border bg-phx-surface-2 text-phx-text-muted text-phx-xs flex shrink-0 items-center gap-2 border-t px-3.5 py-[9px]">
        <span>
          <b className="text-phx-text-2 font-semibold tabular-nums">{sharedCount}</b>{' '}
          {t('costScheme.terms.footerShared')}
        </span>
        <span className="text-phx-text-muted">·</span>
        <span>
          <b className="text-phx-text-2 font-semibold tabular-nums">{customCount}</b>{' '}
          {t('costScheme.terms.footerCustom')}
        </span>
      </div>
    </aside>
  );
}
